import React from 'react';
import {
  EyeIcon,
  LinkIcon,
  PaperAirplaneIcon,
  BanknotesIcon,
  NoSymbolIcon,
  TrashIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { invoicesApi } from '../../utils/api';
import { queryKeys } from '../../hooks/useData';
import { RowActionsMenu, RowAction } from '../ui/RowActionsMenu';
import { useToast } from '../../contexts/ToastContext';

type InvoiceActionsMenuProps = {
  invoice: {
    id: string;
    invoiceNumber: string;
    status: string;
    stripePaymentLinkUrl?: string | null;
    publicToken?: string | null;
  };
  portalBaseUrl?: string;
  onView?: (invoice: InvoiceActionsMenuProps['invoice']) => void;
};

const terminalStatuses = new Set(['PAID', 'VOIDED', 'CANCELLED']);

export function InvoiceActionsMenu({ invoice, portalBaseUrl, onView }: InvoiceActionsMenuProps) {
  const qc = useQueryClient();
  const { showToast } = useToast();

  const portalUrl = invoice.publicToken
    ? `${portalBaseUrl || window.location.origin}/invoice/${invoice.publicToken}`
    : null;

  const refresh = () => qc.invalidateQueries({ queryKey: queryKeys.invoices });

  const runAction = async (
    label: string,
    fn: () => Promise<any>,
    confirmMessage?: string,
  ) => {
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    try {
      await fn();
      refresh();
      showToast(`${label} ✓`, 'success');
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || `${label} failed`;
      showToast(msg, 'error');
    }
  };

  const copyText = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast(`${label} copied`, 'success');
    } catch {
      showToast(text, 'info');
    }
  };

  const actions: RowAction[] = [
    {
      id: 'view',
      label: 'View details',
      icon: EyeIcon,
      onClick: () => onView?.(invoice),
      hidden: !onView,
    },
    {
      id: 'pdf',
      label: 'View PDF',
      icon: ArrowDownTrayIcon,
      onClick: () => {
        window.open(invoicesApi.getPdfUrl(invoice.id), '_blank', 'noopener');
      },
    },
    {
      id: 'copy-link',
      label: 'Copy client link',
      icon: LinkIcon,
      onClick: () => copyText(portalUrl!, 'Client link'),
      hidden: !portalUrl,
    },
    {
      id: 'copy-paylink',
      label: 'Copy pay link',
      icon: LinkIcon,
      onClick: () => copyText(invoice.stripePaymentLinkUrl!, 'Pay link'),
      hidden: !invoice.stripePaymentLinkUrl || terminalStatuses.has(invoice.status),
    },
    {
      id: 'send',
      label:
        invoice.status === 'SENT' || invoice.status === 'VIEWED'
          ? 'Resend to client'
          : 'Send to client',
      icon: PaperAirplaneIcon,
      divider: true,
      hidden: terminalStatuses.has(invoice.status),
      onClick: () => runAction('Sent invoice', () => invoicesApi.send(invoice.id)),
    },
    {
      id: 'mark-paid',
      label: 'Mark as paid',
      icon: BanknotesIcon,
      hidden: terminalStatuses.has(invoice.status),
      onClick: () => {
        const method = window.prompt(
          'Payment method (e.g. BANK_TRANSFER, CASH, STRIPE)?',
          'BANK_TRANSFER',
        );
        if (method === null) return;
        runAction('Marked as paid', () =>
          invoicesApi.markPaid(invoice.id, { paymentMethod: method || undefined }),
        );
      },
    },
    {
      id: 'void',
      label: 'Void invoice',
      icon: NoSymbolIcon,
      danger: true,
      hidden: terminalStatuses.has(invoice.status) || invoice.status === 'DRAFT',
      onClick: () =>
        runAction(
          'Voided invoice',
          () => invoicesApi.voidInvoice(invoice.id),
          'Void this invoice? It will no longer be payable by the client.',
        ),
    },
    {
      id: 'delete',
      label: 'Delete draft',
      icon: TrashIcon,
      danger: true,
      hidden: invoice.status !== 'DRAFT',
      onClick: () =>
        runAction(
          'Deleted draft',
          () => invoicesApi.deleteDraft(invoice.id),
          'Delete this draft invoice? This cannot be undone.',
        ),
    },
  ];

  return <RowActionsMenu actions={actions} label="Invoice actions" />;
}
